import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
// import Navbar from './components/Navbar'; // Removed old navbar
import { NavBar } from './components/ui/tubelight-navbar'; // Imported new navbar
import { Home, Briefcase, Phone, HelpCircle } from 'lucide-react'; // Import icons
import AnalysisModal from './components/AnalysisModal';
import { TestimonialsSection } from './components/blocks/testimonials-with-marquee';
import { HeroGeometric, ElegantShape } from './components/ui/shape-landing-hero';
import { GradientButton } from './components/ui/gradient-button';
import { AnimatedGradient } from "./components/ui/animated-gradient-with-svg"
import { FAQ } from './components/FAQ';

const aiTestimonials = [
  {
    author: {
      name: "Juan Pérez",
      handle: "Dueño de E-commerce",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
    },
    text: "El agente de voz que nos instalaron atiende todas las llamadas de pedidos. Es como tener un empleado 24/7 que nunca se cansa.",
  },
  {
    author: {
      name: "Marta R.",
      handle: "Directora de Marketing",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face"
    },
    text: "Nuestros vídeos cortos ahora llegan a millones de personas. El equipo de Skyrocket AI entiende perfectamente el algoritmo.",
  },
  {
    author: {
      name: "Roberto S.",
      handle: "CEO de TechStart",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face"
    },
    text: "El SaaS a medida que construyeron nos ha permitido ahorrar 20 horas de trabajo manual a la semana. Brutal.",
  }
];

const allServices = [
  { 
    id: 'web', 
    icon: 'fa-code', 
    title: "Páginas Web", 
    description: "Creamos sitios web rápidos y modernos que convierten visitas en clientes.",
    details: ["Diseño exclusivo", "Carga rápida", "Optimizado para Google"]
  },
  { 
    id: 'chatbots', 
    icon: 'fa-robot', 
    title: "Chatbots de IA", 
    description: "Atención al cliente automática por chat que resuelve dudas al instante.",
    details: ["WhatsApp y Web", "Aprende de tu negocio", "Vende por ti"]
  },
  { 
    id: 'voice', 
    icon: 'fa-phone-volume', 
    title: "Agentes de Voz IA", 
    description: "IA que habla por teléfono como un humano para agendar citas o vender.",
    details: ["Voz natural", "Sin esperas", "Integrado con tu CRM"]
  },
  { 
    id: 'saas', 
    icon: 'fa-layer-group', 
    title: "SaaS a Medida", 
    description: "Desarrollamos software específico para automatizar los procesos de tu empresa.",
    details: ["Soluciones únicas", "Escalable", "Panel de control"]
  },
  { 
    id: 'ads', 
    icon: 'fa-ad', 
    title: "Publicidad (Ads)", 
    description: "Campañas en Google, Facebook e Instagram para atraer tráfico de calidad.",
    details: ["Segmentación precisa", "Retorno de inversión", "Análisis semanal"]
  },
  { 
    id: 'videos', 
    icon: 'fa-video', 
    title: "Vídeos Cortos y Largos", 
    description: "Creamos contenido visual (Reels, TikTok, YouTube) para posicionar tu marca.",
    details: ["Edición profesional", "Guiones virales", "Crecimiento orgánico"]
  }
];

const BentoCard = ({ title, value, subtitle, colors, delay }) => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: delay + 0.3,
      },
    },
  }

  const item = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { duration: 0.5 } },
  }

  return (
    <motion.div
      className="relative overflow-hidden h-full rounded-3xl border border-white/10 bg-white/[0.02]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay }}
    >
      <AnimatedGradient colors={colors} speed={0.05} blur="medium" />
      <motion.div
        className="relative z-10 p-6 sm:p-8 backdrop-blur-sm h-full flex flex-col justify-center"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <motion.div variants={item} className="mb-4">
           <i className="fa-solid fa-circle-check text-nebula-gold text-2xl"></i>
        </motion.div>
        <motion.h3 
          className="text-xl sm:text-2xl font-bold mb-3 text-white" 
          variants={item}
        >
          {title}
        </motion.h3>
        <motion.p
          className="text-white/60 leading-relaxed text-sm sm:text-base"
          variants={item}
        >
          {subtitle}
        </motion.p>
      </motion.div>
    </motion.div>
  )
}

const App = () => {
  const [view, setView] = useState('home');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const glowRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (glowRef.current) {
        window.requestAnimationFrame(() => {
          if (glowRef.current) {
            glowRef.current.style.setProperty('--mouse-x', `${e.clientX}px`);
            glowRef.current.style.setProperty('--mouse-y', `${e.clientY}px`);
          }
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const navItems = [
    { name: 'Inicio', url: 'home', icon: Home },
    { name: 'Servicios', url: 'servicios', icon: Briefcase },
    { name: 'FAQ', url: 'faq', icon: HelpCircle },
    { name: 'Contacto', url: 'contact', icon: Phone }
  ];

  const handleNavChange = (url) => {
    if (url === 'contact') {
        setIsContactModalOpen(true);
    } else {
        setView(url);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  const renderHome = () => (
    <>
      <HeroGeometric 
        title1="Lleva tu negocio" 
        title2="al siguiente nivel"
        description="Ayudamos a empresas a crecer usando Inteligencia Artificial y las mejores herramientas digitales del mercado. Sin palabras raras, solo resultados."
      >
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
          <GradientButton 
            onClick={() => setIsContactModalOpen(true)}
            className="shadow-[0_0_30px_rgba(200,178,115,0.2)]"
          >
            Hablemos de tu Proyecto
          </GradientButton>
          <button 
            onClick={() => setView('servicios')}
            className="text-white/60 hover:text-nebula-gold px-8 py-4 font-medium transition-colors flex items-center gap-2 group"
          >
            Ver lo que hacemos
            <i className="fa-solid fa-chevron-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
          </button>
        </div>
      </HeroGeometric>

      <div className="relative w-full">
        {/* Grid overlay specifically for these sections */}
        <div className="absolute inset-0 grid-overlay opacity-40 pointer-events-none z-0"></div>
        
        <section className="px-6 py-24 max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Nuestras <span className="gold-text-gradient">Soluciones Estrella</span></h2>
            <p className="text-white/40">Lo más pedido por nuestros clientes para vender más.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {allServices.slice(0, 3).map((service) => (
              <div key={service.id} className="side-glow-card p-10 bg-white/[0.02] border border-white/5 rounded-3xl transition-all group">
                <div className="w-14 h-14 bg-nebula-gold/5 rounded-2xl flex items-center justify-center mb-8 border border-nebula-gold/10">
                  <i className={`fa-solid ${service.icon} text-2xl text-nebula-gold`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 group-hover:text-nebula-gold transition-colors">{service.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.details.map((d, i) => (
                    <li key={i} className="text-xs text-white/30 flex items-center gap-2">
                      <i className="fa-solid fa-check text-nebula-gold/40"></i> {d}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <GradientButton 
              onClick={() => setView('servicios')}
            >
              VER TODOS LOS SERVICIOS
            </GradientButton>
          </div>
        </section>

        {/* SECCIÓN POR QUÉ NOSOTROS */}
        <section className="px-6 py-24 bg-white/[0.02] border-y border-white/5 relative z-10">
          <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                  <h2 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">Por qué <span className="text-nebula-gold">Skyrocket AI</span> es tu mejor aliado</h2>
                  <p className="text-white/40 max-w-2xl mx-auto">Resultados tangibles, sin complicaciones.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 h-full">
                  <BentoCard
                      title="Resultados Reales"
                      subtitle="No implementamos tecnología por moda. Buscamos que cada euro invertido te traiga un retorno claro."
                      colors={["#c8b273", "#8c734b", "#4a3b2a"]}
                      delay={0.2}
                  />
                  <BentoCard
                      title="Facilidad de Uso"
                      subtitle="Hacemos que lo complejo parezca sencillo. Tú te encargas de tu negocio, nosotros de la tecnología."
                      colors={["#c8b273", "#e0c890", "#3d321d"]}
                      delay={0.4}
                  />
                  <BentoCard
                      title="Acompañamiento Total"
                      subtitle="Estamos contigo en cada paso, desde la idea inicial hasta que la solución está funcionando al 100%."
                      colors={["#8c734b", "#c8b273", "#2d2616"]}
                      delay={0.6}
                  />
              </div>
          </div>
        </section>

        <TestimonialsSection 
          title="Resultados que puedes alcanzar con nosotros"
          description="Historias de éxito que tu empresa puede llegar a protagonizar con nuestra tecnología."
          testimonials={aiTestimonials}
        />
      </div>
    </>
  );

  const renderServicios = () => (
    <section className="pt-48 pb-32 px-6 max-w-7xl mx-auto z-10 relative min-h-screen">
      <div className="absolute inset-0 grid-overlay opacity-40 pointer-events-none z-0"></div>
      
      <div className="relative z-10">
        <div className="mb-16 text-center">
          <h2 className="text-5xl md:text-7xl font-bold mb-6">Todos nuestros <span className="text-nebula-gold">Servicios</span></h2>
          <p className="text-white/50 max-w-2xl mx-auto text-lg">Todo lo que necesitas para que tu empresa funcione sola y venda más en internet.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {allServices.map((service) => (
            <div key={service.id} className="side-glow-card bg-white/[0.02] border border-white/10 rounded-3xl p-10 flex flex-col h-full group hover:border-nebula-gold/30">
              <div className="w-16 h-16 bg-nebula-gold/5 rounded-2xl flex items-center justify-center mb-8 border border-nebula-gold/10">
                <i className={`fa-solid ${service.icon} text-3xl text-nebula-gold`}></i>
              </div>
              <h4 className="text-2xl font-bold text-white mb-4 group-hover:text-nebula-gold transition-colors">{service.title}</h4>
              <p className="text-white/40 text-base mb-8 flex-grow">{service.description}</p>
              <div className="space-y-3 pt-6 border-t border-white/5">
                {service.details.map((detail, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm text-white/60 font-medium">
                    <i className="fa-solid fa-circle-check text-nebula-gold text-[10px]"></i>
                    {detail}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <div className="relative min-h-screen selection:bg-nebula-gold/30 overflow-x-hidden bg-[#050505] text-white">
      <div ref={glowRef} className="fixed inset-0 pointer-events-none z-[11]"
        style={{ background: `radial-gradient(800px circle at var(--mouse-x, -100%) var(--mouse-y, -100%), rgba(200, 178, 115, 0.08), transparent 80%)` }}
      />
      {/* Removed global grid-overlay */}
      <div className="fixed inset-0 nebula-gradient-overlay z-0"></div>

      <NavBar items={navItems} activeTab={view} onTabChange={handleNavChange} />

      <main className="min-h-[calc(100vh-400px)]">
        <AnimatePresence mode="wait">
          <motion.div
            key={view}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            {view === 'home' && renderHome()}
            {view === 'servicios' && renderServicios()}
            {view === 'faq' && <FAQ />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* SECCIÓN CTA RESTAURADA CON SHADERS */}
      <section className="relative overflow-hidden border-t border-white/5 bg-[#050505] py-32 px-6">
        {/* Background shapes (shaders) */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-50">
          <ElegantShape
            delay={0.2}
            width={400}
            height={100}
            rotate={-10}
            gradient="from-nebula-gold/[0.1]"
            className="left-[-5%] bottom-[10%]"
          />
          <ElegantShape
            delay={0.4}
            width={300}
            height={80}
            rotate={15}
            gradient="from-nebula-gold/[0.08]"
            className="right-[-10%] top-[20%]"
          />
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold mb-8">¿Listo para que tu negocio <span className="text-nebula-gold">despegue</span>?</h2>
          <p className="text-white/40 text-lg mb-12">Hacemos que la tecnología trabaje para ti, no al revés. Cuéntanos qué necesitas y lo hacemos realidad.</p>
          <GradientButton 
            onClick={() => setIsContactModalOpen(true)}
            className="shadow-[0_0_50px_rgba(200,178,115,0.2)]"
          >
            CONTACTAR AHORA
          </GradientButton>
        </div>
      </section>

      <footer className="px-6 py-20 border-t border-white/5 max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-12 z-10 relative">
        <div className="absolute inset-0 grid-overlay opacity-40 pointer-events-none z-0"></div>
        <div className="relative z-10 space-y-4 text-center md:text-left">
          <div className="text-white font-bold text-2xl tracking-tighter">SKYROCKET<span className="text-nebula-gold">AI</span></div>
          <p className="text-white/20 text-sm max-w-xs">Expertos en digitalizar y automatizar negocios para el mundo moderno.</p>
        </div>
        <div className="relative z-10 flex gap-12 text-sm text-white/40 font-bold uppercase tracking-widest text-[10px]">
           <button onClick={() => setView('home')} className="hover:text-nebula-gold transition-colors">Inicio</button>
           <button onClick={() => setView('servicios')} className="hover:text-nebula-gold transition-colors">Servicios</button>
           <button onClick={() => setIsContactModalOpen(true)} className="hover:text-nebula-gold transition-colors">Contacto</button>
        </div>
        <div className="relative z-10 text-white/20 text-xs">
          © 2024 Skyrocket AI. Despega tu negocio.
        </div>
      </footer>

      <AnalysisModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </div>
  );
};

export default App;
